import React, { useEffect } from "react";
import PropTypes from "prop-types";
// @mui
import {
  Stack,
  Drawer,
  Divider,
  Typography,
  TextField,
  Box,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  Checkbox,
  ListItemText,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";

// components
import { LoadingButton } from "@mui/lab";
import { Close } from "@mui/icons-material";
import {
  useCreateGoogleAnalyticsConfigMutation,
  useUpdateGoogleAnalyticsConfigMutation,
} from "../../../redux/services/googleAnalyticsConfig.service";
import { useGetGoogleAnalyticsSectionsQuery } from "../../../redux/services/googleAnalyticsSection.service";
import { useGetRolesQuery } from "../../../redux/services/role.service";
import { useGetAuthUsersQuery } from "../../../redux/services/authUser.service";
import Scrollbar from "../../../components/scrollbar";

const ConfigSchema = yup.object().shape({
  keyName: yup.string().required("Key name is required"),
  graphId: yup.string(),
  isAccessible: yup.boolean(),
  roleIds: yup.array().of(yup.string()),
  authUserIds: yup.array().of(yup.string()),
});

GoogleAnalyticsConfigForm.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  edit: PropTypes.bool,
  openRow: PropTypes.object,
};

export default function GoogleAnalyticsConfigForm({
  open,
  onClose,
  edit,
  openRow,
}) {
  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    resolver: yupResolver(ConfigSchema),
    defaultValues: {
      keyName: "",
      graphId: "",
      isAccessible: true,
      roleIds: [],
      authUserIds: [],
    },
  });

  const [createConfig, { isLoading }] =
    useCreateGoogleAnalyticsConfigMutation();
  const [updateConfig, { isLoading: isUpdateLoading }] =
    useUpdateGoogleAnalyticsConfigMutation();

  const { data: sectionsData, isLoading: isLoadingSections } =
    useGetGoogleAnalyticsSectionsQuery({});
  const { data: rolesData, isLoading: isLoadingRoles } = useGetRolesQuery({});
  const { data: authUsersData, isLoading: isLoadingAuthUsers } =
    useGetAuthUsersQuery({ isAuthAllInfo: true });

  const handleClick = async (values) => {
    let resultAction;
    if (edit) {
      // Send isAccessible, roleIds, and authUserIds for update (empty array if none selected)
      const payload = {
        isAccessible: values.isAccessible,
        roleIds: values.roleIds || [],
        authUserIds: values.authUserIds || [],
      };
      resultAction = await updateConfig({ ...payload, id: openRow.id });
    } else {
      // Prepare full payload for create - always send roleIds and authUserIds (empty array if none selected)
      const payload = {
        keyName: values.keyName, // Required: string
        ...(values.graphId && { graphId: values.graphId }), // Optional: string (UUID)
        isAccessible: values.isAccessible, // Boolean
        roleIds: values.roleIds || [], // UUID array (empty if none selected)
        authUserIds: values.authUserIds || [], // UUID array (empty if none selected)
      };
      resultAction = await createConfig(payload);
    }
    if (resultAction?.data?.success) {
      onClose();
      reset();
    }
  };

  useEffect(() => {
    if (edit && openRow) {
      setValue("keyName", openRow.keyName);
      setValue("graphId", openRow.graphId || "");
      setValue("isAccessible", openRow.isAccessible ?? true);
      setValue("roleIds", openRow.roleIds || []);
      setValue("authUserIds", openRow.authUserIds || []);
    } else {
      reset({
        keyName: "",
        graphId: "",
        isAccessible: true,
        roleIds: [],
        authUserIds: [],
      });
    }
  }, [edit, reset, openRow, setValue, open]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: 480, border: "none", overflow: "hidden" },
      }}
    >
      <Scrollbar>
        <Stack spacing={3} sx={{ p: 3 }}>
          <div>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 0.5,
              }}
            >
              <Typography variant="subtitle1">
                {edit ? "Edit" : "Create"} Google Analytics Config
              </Typography>
              <IconButton onClick={onClose}>
                <Close />
              </IconButton>
            </Box>
            <Divider />
          </div>
          <form onSubmit={handleSubmit(handleClick)}>
            <Stack spacing={3}>
              {/* Key Name - Disabled in edit mode */}
              <TextField
                name="keyName"
                label="Key Name"
                {...register("keyName", { required: !edit })}
                error={Boolean(errors.keyName)}
                helperText={
                  edit
                    ? ""
                    : errors.keyName?.message ||
                      "Field name like objectCount, totalSessions, etc."
                }
                disabled={edit}
                inputProps={{ "data-testid": "keyName" }}
                placeholder="e.g., totalObjectCount, totalSessions"
              />

              {/* Section Name - Show as disabled TextField in edit mode */}
              {edit ? (
                <TextField
                  label="Section Name"
                  value={openRow?.graphName || "None"}
                  disabled
                  fullWidth
                />
              ) : (
                <FormControl fullWidth>
                  <InputLabel id="graphId-label">Section Name</InputLabel>
                  <Controller
                    name="graphId"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        labelId="graphId-label"
                        label="Section Name"
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {isLoadingSections ? (
                          <MenuItem disabled>Loading...</MenuItem>
                        ) : (
                          sectionsData?.data?.map((section) => (
                            <MenuItem key={section.id} value={section.id}>
                              {section.graphName}
                            </MenuItem>
                          ))
                        )}
                      </Select>
                    )}
                  />
                </FormControl>
              )}

              {/* Is Accessible - Always editable */}
              <Controller
                name="isAccessible"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        {...field}
                        checked={field.value}
                        color="primary"
                      />
                    }
                    label="Is Accessible"
                  />
                )}
              />

              {/* Roles - Editable in both create and edit mode */}
              <FormControl fullWidth>
                <InputLabel id="roleIds-label">Roles</InputLabel>
                <Controller
                  name="roleIds"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      labelId="roleIds-label"
                      label="Roles"
                      multiple
                      input={<OutlinedInput label="Roles" />}
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => {
                            const role = rolesData?.data?.find(
                              (r) => r.id === value,
                            );
                            return (
                              <Chip key={value} label={role?.name || value} />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {isLoadingRoles ? (
                        <MenuItem disabled>Loading...</MenuItem>
                      ) : (
                        rolesData?.data?.map((role) => (
                          <MenuItem key={role.id} value={role.id}>
                            <Checkbox checked={field.value.includes(role.id)} />
                            <ListItemText primary={role.name} />
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  )}
                />
              </FormControl>

              {/* Auth Users - Editable in both create and edit mode */}
              <FormControl fullWidth>
                <InputLabel id="authUserIds-label">Auth Users</InputLabel>
                <Controller
                  name="authUserIds"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      labelId="authUserIds-label"
                      label="Auth Users"
                      multiple
                      input={<OutlinedInput label="Auth Users" />}
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => {
                            const user = authUsersData?.data?.find(
                              (u) => u.id === value,
                            );
                            return (
                              <Chip
                                key={value}
                                label={user?.email || value}
                                size="small"
                              />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {isLoadingAuthUsers ? (
                        <MenuItem disabled>Loading...</MenuItem>
                      ) : (
                        authUsersData?.data?.map((user) => (
                          <MenuItem key={user.id} value={user.id}>
                            <Checkbox checked={field.value.includes(user.id)} />
                            <ListItemText
                              primary={user.email}
                              secondary={`${user.firstName || ""} ${
                                user.lastName || ""
                              }`}
                            />
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  )}
                />
              </FormControl>

              <LoadingButton
                type="submit"
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                loading={edit ? isUpdateLoading : isLoading}
                data-testid="submitBtn"
              >
                Submit
              </LoadingButton>
            </Stack>
          </form>
        </Stack>
      </Scrollbar>
    </Drawer>
  );
}
